import React, { useState } from 'react';
import { Search, FileText, Calendar, ListChecks, BookOpen, LayoutDashboard, Play, Scale, GraduationCap, BookMarked, Landmark, Pill, Presentation, ChevronDown, ChevronRight, Loader, Check, X } from 'lucide-react';
import type { AgentToolCall } from '../../contexts/AskSidebarContext';
import { WIDGET_REGISTRY } from './widgets';

interface ToolMeta {
  icon: React.FC<{ className?: string }>;
  label: string;
  activeLabel: string;
  color: string;
}

const TOOL_META: Record<string, ToolMeta> = {
  search_documents: { icon: Search, label: 'Cross-referenced records', activeLabel: 'Cross-referencing records', color: 'text-cyan-600 bg-cyan-50 dark:text-cyan-400 dark:bg-cyan-900/20' },
  list_documents: { icon: ListChecks, label: 'Indexed available records', activeLabel: 'Indexing records', color: 'text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-900/20' },
  read_document: { icon: FileText, label: 'Reviewed document', activeLabel: 'Reviewing document', color: 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/20' },
  get_chronology: { icon: Calendar, label: 'Examined timeline', activeLabel: 'Examining timeline', color: 'text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/20' },
  list_analyses: { icon: ListChecks, label: 'Reviewed prior analyses', activeLabel: 'Reviewing analyses', color: 'text-purple-600 bg-purple-50 dark:text-purple-400 dark:bg-purple-900/20' },
  read_analysis: { icon: BookOpen, label: 'Examined analysis findings', activeLabel: 'Examining analysis', color: 'text-purple-600 bg-purple-50 dark:text-purple-400 dark:bg-purple-900/20' },
  get_case_overview: { icon: LayoutDashboard, label: 'Assessed case profile', activeLabel: 'Assessing case', color: 'text-medical-600 bg-medical-50 dark:text-medical-400 dark:bg-medical-800' },
  trigger_analysis: { icon: Play, label: 'Initiated deep analysis', activeLabel: 'Running analysis', color: 'text-rose-600 bg-rose-50 dark:text-rose-400 dark:bg-rose-900/20' },
  search_guidelines: { icon: Scale, label: 'Consulted clinical guidelines', activeLabel: 'Consulting guidelines', color: 'text-teal-600 bg-teal-50 dark:text-teal-400 dark:bg-teal-900/20' },
  search_pubmed: { icon: GraduationCap, label: 'Queried medical literature', activeLabel: 'Querying literature', color: 'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-900/20' },
  lookup_abbreviation: { icon: BookMarked, label: 'Looked up abbreviation', activeLabel: 'Looking up abbreviation', color: 'text-violet-600 bg-violet-50 dark:text-violet-400 dark:bg-violet-900/20' },
  lookup_limitation_period: { icon: Landmark, label: 'Checked limitation period', activeLabel: 'Checking limitation period', color: 'text-slate-600 bg-slate-50 dark:text-slate-400 dark:bg-slate-800' },
  search_case_law: { icon: Scale, label: 'Searched case law', activeLabel: 'Searching case law', color: 'text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/20' },
  check_drug_interactions: { icon: Pill, label: 'Checked drug interactions', activeLabel: 'Checking drug interactions', color: 'text-rose-600 bg-rose-50 dark:text-rose-400 dark:bg-rose-900/20' },
  present_data: { icon: Presentation, label: 'Prepared visual', activeLabel: 'Preparing visual', color: 'text-cyan-600 bg-cyan-50 dark:text-cyan-400 dark:bg-cyan-900/20' },
};

function getToolDescription(tc: AgentToolCall): string {
  const input = tc.input || {};
  switch (tc.toolName) {
    case 'search_documents':
    case 'search_pubmed':
      return input.query ? `"${input.query}"` : '';
    case 'search_guidelines':
      return input.query ? `"${input.query}"` : (input.category ? String(input.category) : '');
    case 'read_document':
      return input.document_id ? String(input.document_id).substring(0, 8) + '...' : '';
    case 'read_analysis':
      return input.objective_id ? String(input.objective_id).substring(0, 8) + '...' : '';
    case 'trigger_analysis':
      return input.analysis_type ? String(input.analysis_type).replace(/_/g, ' ') : '';
    case 'lookup_abbreviation':
      return input.abbreviation ? String(input.abbreviation).toUpperCase() : '';
    case 'lookup_limitation_period':
      return input.query ? `"${input.query}"` : '';
    case 'search_case_law':
      return input.query ? `"${input.query}"` : '';
    case 'check_drug_interactions':
      return input.drug1 && input.drug2 ? `${input.drug1} + ${input.drug2}` : '';
    default:
      return tc.summary || '';
  }
}

/** Build a short summary using active labels when running, past tense when done. */
function buildSummary(toolCalls: AgentToolCall[], useActiveLabel: boolean): string {
  const counts: Record<string, number> = {};
  for (const tc of toolCalls) {
    const meta = TOOL_META[tc.toolName];
    const label = meta ? (useActiveLabel ? meta.activeLabel : meta.label) : tc.toolName;
    counts[label] = (counts[label] || 0) + 1;
  }
  const parts = Object.entries(counts).map(([label, count]) =>
    count > 1 ? `${label} ×${count}` : label
  );
  return parts.join(', ');
}

interface Props {
  toolCalls: AgentToolCall[];
  variant?: 'compact' | 'full';
}

export const AgentToolCallIndicator: React.FC<Props> = ({ toolCalls, variant = 'compact' }) => {
  const [expanded, setExpanded] = useState(false);

  if (!toolCalls || toolCalls.length === 0) return null;

  const hasRunning = toolCalls.some(tc => tc.status === 'running');
  const allDone = toolCalls.every(tc => tc.status === 'done' || tc.status === 'error');

  if (variant === 'compact') {
    return (
      <div className="my-1.5">
        {/* Collapsed summary toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-[11px] text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-300 transition-colors"
        >
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {hasRunning ? (
            <Loader className="h-3 w-3 animate-spin text-purple-500" />
          ) : (
            <Check className="h-3 w-3 text-green-500" />
          )}
          <span className="font-medium truncate max-w-[250px]">
            {hasRunning
              ? `${buildSummary(toolCalls, true)}...`
              : `${buildSummary(toolCalls, false)}`}
          </span>
        </button>

        {/* Expanded: individual pills */}
        {expanded && (
          <div className="flex flex-wrap gap-1 mt-1.5 ml-4">
            {toolCalls.map((tc) => {
              const meta = TOOL_META[tc.toolName] || { icon: Search, label: tc.toolName, color: 'text-medical-600 bg-medical-50 dark:text-medical-400 dark:bg-medical-800' };
              const Icon = meta.icon;
              const desc = getToolDescription(tc);
              const StatusIcon = tc.status === 'running' ? Loader : tc.status === 'error' ? X : tc.status === 'done' ? Check : null;

              return (
                <span
                  key={tc.toolUseId}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.color}`}
                  title={desc || meta.label}
                >
                  <Icon className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate max-w-[120px]">{desc || meta.label}</span>
                  {StatusIcon && <StatusIcon className={`h-2.5 w-2.5 flex-shrink-0 ${tc.status === 'running' ? 'animate-spin' : ''}`} />}
                </span>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Full variant — wider cards with expand/collapse and widget rendering
  return (
    <div className="my-2 space-y-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-300"
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {hasRunning ? (
          <Loader className="h-3 w-3 animate-spin text-purple-500" />
        ) : (
          <Check className="h-3 w-3 text-green-500" />
        )}
        <span className="font-medium">
          {hasRunning
            ? `${buildSummary(toolCalls, true)}...`
            : `${buildSummary(toolCalls, false)}`}
        </span>
      </button>

      {expanded && (
        <div className="space-y-1.5 ml-4">
          {toolCalls.map((tc) => {
            const meta = TOOL_META[tc.toolName] || { icon: Search, label: tc.toolName, color: 'text-medical-600 bg-medical-50 dark:text-medical-400 dark:bg-medical-800' };
            const Icon = meta.icon;
            const desc = getToolDescription(tc);
            const StatusIcon = tc.status === 'running' ? Loader : tc.status === 'error' ? X : tc.status === 'done' ? Check : null;

            // Look up widget from registry
            const widgetType = tc.widgetData?.type;
            const Widget = widgetType ? WIDGET_REGISTRY[widgetType] : null;

            return (
              <div key={tc.toolUseId} className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${meta.color} border-current/10`}>
                <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold">{meta.label}</span>
                    {StatusIcon && <StatusIcon className={`h-3 w-3 ${tc.status === 'running' ? 'animate-spin' : ''}`} />}
                  </div>
                  {desc && <p className="text-[11px] opacity-80 truncate">{desc}</p>}

                  {/* Render widget if available, otherwise fall back to summary text */}
                  {tc.status === 'done' && Widget && tc.widgetData ? (
                    <Widget data={tc.widgetData} />
                  ) : tc.summary && tc.summary !== desc && tc.status === 'done' ? (
                    <p className="text-[11px] opacity-70 mt-0.5 line-clamp-2">{tc.summary}</p>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
